import type { SubscribedSymbolEntry } from "@/vybe-trader-client/subscribed-symbols/types";

export interface WatchlistQuoteDisplay {
  symbol: string;
  bid: number;
  ask: number;
  dayPercent: number;
  points: number;
  low: number;
  high: number;
  time: string;
  bidHigh: number;
  bidLow: number;
  askHigh: number;
  askLow: number;
  last: number;
  lastHigh: number;
  lastLow: number;
  spread: number;
  // Last-tick direction per price column: +1 up, -1 down, 0 unchanged.
  bidDirection: number;
  askDirection: number;
  lastDirection: number;
}

/** Signed last-tick direction: +1 up, -1 down, 0 unchanged. */
function tickDirection(current: number, prev: number | undefined): number {
  if (prev === undefined) return 0;
  if (current > prev) return 1;
  if (current < prev) return -1;
  return 0;
}

export function formatWatchlistPrice(price: number, symbol: string): string {
  let decimals = 5;
  if (symbol.includes("JPY")) decimals = 3;
  else if (symbol === "GOLD" || symbol.includes("GOLD")) decimals = 2;
  return price.toFixed(decimals);
}

export function formatWatchlistPercent(percent: number): string {
  return `${percent.toFixed(2)}%`;
}

export function buildWatchlistQuoteDisplay(
  entry: SubscribedSymbolEntry
): WatchlistQuoteDisplay {
  const quote = entry.quote;
  const symbol = entry.symbol;
  const spread = quote ? quote.ask - quote.bid : 0;
  const points =
    quote && quote.last > 0
      ? Math.round((quote.bid - quote.last) * Math.pow(10, symbol.digits))
      : 0;
  const dayPercent =
    quote && quote.lastPrice > 0
      ? ((quote.bid - quote.lastPrice) / quote.lastPrice) * 100
      : 0;
  const time = quote
    ? new Date(quote.time).toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : "00:00:00";

  const bid = quote?.bid ?? 0;
  const ask = quote?.ask ?? 0;
  const last = quote?.last || bid;

  const bidDirection = quote ? tickDirection(bid, quote.prevBid) : 0;
  const askDirection = quote ? tickDirection(ask, quote.prevAsk) : 0;
  const lastDirection = quote ? tickDirection(last, quote.prevLast) : 0;

  return {
    symbol: symbol.currency,
    bid,
    ask,
    dayPercent,
    points,
    low: bid,
    high: ask,
    time,
    bidHigh: quote ? quote.bid * 1.001 : 0,
    bidLow: quote ? quote.bid * 0.999 : 0,
    askHigh: quote ? quote.ask * 1.001 : 0,
    askLow: quote ? quote.ask * 0.999 : 0,
    last: quote?.last || bid,
    lastHigh: quote?.last ? quote.last * 1.001 : bid * 1.001,
    lastLow: quote?.last ? quote.last * 0.999 : bid * 0.999,
    spread,
    bidDirection,
    askDirection,
    lastDirection,
  };
}

/** Map a signed tick direction to a design-system price-text color class. */
function tickDirectionColor(direction: number): string {
  if (direction > 0) return "text-blue-400";
  if (direction < 0) return "text-red-400";
  return "text-foreground/90";
}

export function getWatchlistColumnValue(
  quote: WatchlistQuoteDisplay,
  columnName: string
): string {
  switch (columnName) {
    case "Bid":
      return formatWatchlistPrice(quote.bid, quote.symbol);
    case "Ask":
      return formatWatchlistPrice(quote.ask, quote.symbol);
    case "Day %":
      return formatWatchlistPercent(quote.dayPercent);
    case "Bid High":
      return formatWatchlistPrice(quote.bidHigh, quote.symbol);
    case "Bid Low":
      return formatWatchlistPrice(quote.bidLow, quote.symbol);
    case "Ask High":
      return formatWatchlistPrice(quote.askHigh, quote.symbol);
    case "Ask Low":
      return formatWatchlistPrice(quote.askLow, quote.symbol);
    case "Last":
      return formatWatchlistPrice(quote.last, quote.symbol);
    case "Last High":
      return formatWatchlistPrice(quote.lastHigh, quote.symbol);
    case "Last Low":
      return formatWatchlistPrice(quote.lastLow, quote.symbol);
    case "Time":
      return quote.time;
    case "Spread":
      return formatWatchlistPrice(Math.abs(quote.spread), quote.symbol);
    default:
      return "-";
  }
}

export function getWatchlistColumnColor(
  quote: WatchlistQuoteDisplay,
  columnName: string
): string {
  switch (columnName) {
    // Bid / Ask / Last are colored by LAST-TICK direction (up -> blue-400,
    // down -> red-400, unchanged -> neutral) — mirrors OpenPositionLiveCells.
    // The Ask column is no longer hard-coded red.
    case "Bid":
    case "Bid High":
    case "Bid Low":
      return tickDirectionColor(quote.bidDirection);
    case "Ask":
    case "Ask High":
    case "Ask Low":
      return tickDirectionColor(quote.askDirection);
    case "Last":
    case "Last High":
    case "Last Low":
      return tickDirectionColor(quote.lastDirection);
    // Day % stays a session-sign signal (not a per-tick direction).
    case "Day %":
      return quote.dayPercent >= 0 ? "text-blue-400" : "text-red-400";
    default:
      return "text-foreground/90";
  }
}
